import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/ui/Modal';
import CTAButton from '@/components/ui/CTAButton';
import IconButton from '@/components/ui/IconButton';
import { ContactCaseLink, StatusTag } from '@/components/ui/shared';
import api from '@/services/api';
import useAuthStore from '@/store/auth';
import usePermissions from '@/hooks/usePermissions';
import { WartenderKundeEintrag } from '@/types';

/**
 * Popup „Mandant wartet" (Paket 1, docs/tasks/belieferung-doppelbelegung.md
 * §5N.2; E-4, E-7, E-20 bis E-23, E-27).
 *
 * Fragt `GET /meetings/wartende-kunden` im Takt, den der Server mitschickt
 * (`takt_sekunden`: 2 s im Fenster einer eigenen EB, sonst 30 s; §5N.2c) —
 * der Server liefert nur Erstberatungen des eingeloggten Users und
 * entscheidet ueber Oeffnen/Schliessen.
 * Hier lebt nur das X: es schliesst genau einen Eintrag, gemerkt an
 * (Termin, Grund, offen seit). Ein NEUES Warte-Signal hat ein neues
 * `offen_seit` und oeffnet den Eintrag wieder.
 *
 * Gefragt wird NUR fuer Rollen, die eigene Zoom-Termine fuehren — positiv
 * VP oder SA (CLAUDE.md §5: keine Rollen-Blacklist). Jede andere Rolle
 * (heute CC, Telefonie; Telefonie bekaeme 403) loest keinen einzigen Request
 * aus, auch eine kuenftige neue Rolle nicht.
 *
 * Nicht blockierend (Modal `nichtBlockierend`): „Ändern" springt in den
 * Kalender, und dort muss der Termin sich ziehen lassen, waehrend das Popup
 * noch steht.
 */

export const POLL_MS = 30_000;
/** Schalter aus: seltener nachsehen, ob er umgelegt wurde. */
export const POLL_AUS_MS = 300_000;
/** Untergrenze gegen einen Konfigurationsfehler am Server (0 oder negativ). */
const POLL_MIN_MS = 1_000;

/**
 * Takt der naechsten Abfrage (Entscheid Alex 13.10.2026, §5N.2c): der Server
 * rechnet ihn aus — 2 s, solange eine eigene EB im Fenster −10 … +15 min liegt,
 * sonst 30 s. Fehlt der Wert (alter Server), bleibt es bei 30 s.
 */
export const taktAusAntwort = (taktSekunden: number | undefined) =>
  typeof taktSekunden === 'number' && Number.isFinite(taktSekunden)
    ? Math.max(POLL_MIN_MS, taktSekunden * 1000)
    : POLL_MS;
/** So lange merkt sich ein Browser ein X — laenger lebt kein Eintrag (Beginn + 60 min). */
const MERK_MS = 3 * 60 * 60 * 1000;
const ZONE = 'Europe/Berlin';

export const schluessel = (e: Pick<WartenderKundeEintrag, 'meeting_id' | 'grund' | 'offen_seit'>) =>
  `${e.meeting_id}|${e.grund}|${e.offen_seit}`;

const speicherName = (userId: number | undefined) => `bw.kundeWartet.geschlossen.${userId ?? 'x'}`;

// Browser-Speicher kann fehlen oder werfen (privates Fenster) — dann gilt das
// X nur bis zum Neuladen, das Popup funktioniert trotzdem.
const leseGeschlossen = (name: string): Record<string, number> => {
  try {
    const roh = JSON.parse(window.localStorage.getItem(name) ?? '{}') as Record<string, number>;
    const jetzt = Date.now();

    return Object.fromEntries(Object.entries(roh).filter(([, bis]) => typeof bis === 'number' && bis > jetzt));
  } catch {
    return {};
  }
};

const schreibeGeschlossen = (name: string, werte: Record<string, number>) => {
  try {
    window.localStorage.setItem(name, JSON.stringify(werte));
  } catch {
    // siehe leseGeschlossen
  }
};

/**
 * Reihenfolge (Sichttest Alex 08.10.2026, zweite Runde): erst alle Wartenden,
 * darunter „Beginnt jetzt"; innerhalb nach Terminuhrzeit, bei gleicher Uhrzeit
 * nach Wartebeginn. Zwei Mandanten derselben Uhrzeit stehen so direkt
 * untereinander. Reine Anzeige — der Server liefert unveraendert.
 */
export const sortiert = (liste: WartenderKundeEintrag[]) =>
  [...liste].sort((a, b) =>
    (a.grund === 'warteraum' ? 0 : 1) - (b.grund === 'warteraum' ? 0 : 1)
    || Date.parse(a.uhrzeit) - Date.parse(b.uhrzeit)
    || Date.parse(a.offen_seit) - Date.parse(b.offen_seit)
    || a.meeting_id - b.meeting_id);

const zeit = (iso: string) =>
  new Date(iso).toLocaleTimeString('de-DE', { timeZone: ZONE, hour: '2-digit', minute: '2-digit' });

/** Kalendertag des Termins in Berlin, `YYYY-MM-DD` (schwedisches Format = ISO). */
export const tagBerlin = (iso: string) => new Date(iso).toLocaleDateString('sv-SE', { timeZone: ZONE });

const KundeWartetPopup: FC = () => {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  const { isVP, isSA } = usePermissions();
  const darfFragen = isVP() || isSA();
  const name = speicherName(userId);

  const [eintraege, setEintraege] = useState<WartenderKundeEintrag[]>([]);
  const [geschlossen, setGeschlossen] = useState<Record<string, number>>(() => leseGeschlossen(name));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const abfragen = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    let naechste = POLL_MS;
    // Verdeckter Tab: nicht fragen — beim Zurueckholen fragt visibilitychange sofort.
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
      try {
        const antwort = await api.meeting.getWartendeKunden();
        setEintraege(antwort.aktiv ? antwort.eintraege : []);
        naechste = antwort.aktiv ? taktAusAntwort(antwort.takt_sekunden) : POLL_AUS_MS;
      } catch {
        // still — der naechste Takt versucht es wieder
      }
    }
    timer.current = setTimeout(abfragen, naechste);
  }, []);

  useEffect(() => {
    if (!darfFragen) return undefined;
    abfragen();
    const beiSichtbar = () => {
      if (document.visibilityState === 'visible') abfragen();
    };
    document.addEventListener('visibilitychange', beiSichtbar);

    return () => {
      if (timer.current) clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', beiSichtbar);
    };
  }, [abfragen, darfFragen]);

  useEffect(() => {
    setGeschlossen(leseGeschlossen(name));
  }, [name]);

  const schliessen = (e: WartenderKundeEintrag) => {
    const neu = { ...leseGeschlossen(name), [schluessel(e)]: Date.now() + MERK_MS };
    schreibeGeschlossen(name, neu);
    setGeschlossen(neu);
  };

  const offen = darfFragen ? sortiert(eintraege.filter((e) => !geschlossen[schluessel(e)])) : [];
  if (offen.length === 0) return null;

  const wartend = offen.filter((e) => e.grund === 'warteraum').length;
  const titel = wartend > 0
    ? (offen.length === 1 ? 'Mandant wartet' : `${offen.length} Termine — ${wartend} wartend`)
    : (offen.length === 1 ? 'Termin beginnt jetzt' : `${offen.length} Termine beginnen jetzt`);

  return (
    <Modal open persistent nichtBlockierend onClose={() => undefined} title={titel} maxWidth="xs" fullWidth>
      <Stack gap={4} data-testid="kunde-wartet-popup">
        {offen.map((e) => (
          // Aufbau nach dem Schwester-Popup `AdminAlarmModal` (gleiche
          // Layout-Ebene): Eintraege mit Trennlinie, Kopfzeile, Meta-Zeile
          // 12px text.secondary, Knoepfe als CTAButton. Alle Zeilen beginnen
          // auf derselben linken Kante.
          <Stack
            key={schluessel(e)}
            gap={1}
            data-testid="kunde-wartet-eintrag"
            sx={{ pt: 4, borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { pt: 0, borderTop: 'none' } }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="kartentitel">{zeit(e.uhrzeit)}</Typography>
                {e.grund === 'warteraum' ? (
                  <Box component="span" data-testid="kunde-wartet-schild-wartet">
                    <StatusTag variant="red" gefuellt pulsPunkt label={`Wartet seit ${zeit(e.offen_seit)}`} />
                  </Box>
                ) : (
                  <Box component="span" data-testid="kunde-wartet-schild-beginnt">
                    <StatusTag variant="blue" label="Beginnt jetzt" />
                  </Box>
                )}
              </Stack>
              <IconButton icon="Close" ariaLabel="Eintrag schließen" onClick={() => schliessen(e)} />
            </Stack>
            <Box data-testid="kunde-wartet-name">
              <ContactCaseLink dealId={e.deal_id} firstName={e.kunde_name ?? 'Ohne Namen'} lastName={null} />
            </Box>
            <Typography variant="klein" data-testid="kunde-wartet-telefon">
              {e.telefon ?? 'Keine Telefonnummer'}
            </Typography>
            {/* Haupt- und Nebenaktion nach DESIGN_SYSTEM „Buttons": gefuellt
                (`secondary`) + umrandet (`outlined`), gleiche Groesse und
                Schrift; `alignItems: stretch` gleicht die 1,5px des Rahmens aus,
                damit beide gleich hoch stehen. Der Start-Knopf an anderen
                Stellen (MeetingStartButton) bleibt unveraendert. */}
            <Stack direction="row" gap={2} alignItems="stretch" sx={{ pt: 1 }}>
              <CTAButton
                variant="secondary"
                size="small"
                label="Starten"
                autoWidth
                onClick={() => window.open(e.zoom_link, '_blank', 'noopener,noreferrer')}
                data-testid="kunde-wartet-starten"
              />
              <CTAButton
                variant="outlined"
                size="small"
                label="Ändern"
                autoWidth
                onClick={() => navigate(
                  `/home/calendar?termin=${e.meeting_id}&datum=${tagBerlin(e.uhrzeit)}`,
                )}
                data-testid="kunde-wartet-aendern"
              />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Modal>
  );
};

export default KundeWartetPopup;
